import { GameRangeFilterOption, PeriodRange } from '@statsbomb/parachute-types';
import { Atom, Getter } from 'jotai';
import { defaultSeasonsAtom } from '@/atoms/filters/highLevel/seasons';
import { PromiseAtom } from '@/types/atom';
import { FormattedGame } from '@/types/game';
import { NumberOption } from '@/types/generic';
import { filterGamesBySelectedGameRange } from '@/utils/filters';
import { findById } from '../array';

export interface DateRangeFilters {
  startDate: string;
  endDate: string;
  gameRange: GameRangeFilterOption;
  periodRange: PeriodRange;
}

export const getFilteredGames = async (
  get: Getter,
  entityGamesAtom: PromiseAtom<FormattedGame[]>,
  filterFunctionsAtom: Atom<(data: FormattedGame[]) => FormattedGame[]>,
  { startDate, endDate, gameRange: selectedGameRange, periodRange: selectedPeriodRange }: DateRangeFilters,
): Promise<FormattedGame[]> => {
  const entityGames = await get(entityGamesAtom);
  const filterFunctions = get(filterFunctionsAtom);
  const defaultSeasons = get(defaultSeasonsAtom);

  // Until the default seasons are loaded, we want to behave as if we have no games selected. This
  // will ensure that we don't fire any API calls to fetch event data until we have the default seasons.
  if (!defaultSeasons) return [];

  const filteredGames = filterFunctions(entityGames);
  const sortedEntityGames = [...filteredGames].sort(
    // Non-null Assertion is used here as games should always have a gameDate at this stage
    (gameB, gameA) => +new Date(gameB.game.date!) - +new Date(gameA.game.date!),
  );

  return filterGamesBySelectedGameRange(sortedEntityGames, selectedGameRange, selectedPeriodRange, startDate, endDate);
};

export const getGamesBySeason = async (
  get: Getter,
  seasonOptionsAtom: PromiseAtom<NumberOption[]>,
  competitionOptionsAtom: PromiseAtom<NumberOption[]>,
  filteredGamesAtom: PromiseAtom<FormattedGame[]>,
) => {
  const seasons = await get(seasonOptionsAtom);
  const competitions = await get(competitionOptionsAtom);
  const filteredGames = await get(filteredGamesAtom);
  const seasonIds = [...new Set(filteredGames.map(({ cycle }) => cycle.seasonId))];

  return seasonIds
    .map(seasonId => {
      const games = [...filteredGames]
        .filter(({ cycle }) => cycle.seasonId === seasonId)
        // Non-null Assertion is used here as games should always have a gameDate at this stage
        .sort((gameA, gameB) => +new Date(gameB.game.date!) - +new Date(gameA.game.date!));

      const gamesWithCompetitionName = games.map(playerGame => ({
        ...playerGame,
        game: {
          ...playerGame.game,
          competitionName: findById(competitions, playerGame.cycle.competitionId, 'value')?.label,
        },
      }));

      return {
        seasonId,
        seasonName: findById(seasons, games[0].cycle.seasonId, 'value')?.label!,
        games: gamesWithCompetitionName,
      };
    })
    .sort((seasonA, seasonB) => seasonB?.seasonName?.localeCompare(seasonA?.seasonName));
};

export const getSelectedGames = async (
  get: Getter,
  gamesOptionsAtom: PromiseAtom<NumberOption[]>,
  deselectedGames: NumberOption[],
) => {
  const gameOptionsInRange = await get(gamesOptionsAtom);

  if (deselectedGames.length === 0) return gameOptionsInRange;

  return gameOptionsInRange.filter(
    option => !deselectedGames.map(deselectedOption => deselectedOption.value).includes(option.value),
  );
};
